import React, { FC, useState } from "react";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { Button, Hidden, Popover, PopoverOrigin, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { CheckIcon, Cross, FilterIcon } from "@/assets/icons";
import { LineSeparator } from "@/components/atoms";
import { IFilterItem, LocalFilterType, LocalFilterValueType } from "@/shared/contexts";
import { useLocalFilter } from "@/shared/hooks/useLocalFilter";

import DateRangeForm from "./components/DateRangeForm/DateRangeForm";
import {
    CleanFilter,
    CurrentFilterContainer,
    FilterCheck,
    FilterContainer,
    FilterCount,
    FilterCountNumber,
    FilterTitle,
    MobileFilterHeader,
    StyledFilterDropdown,
} from "./styled";

type FilterDropdownProps = {
    title: string;
    filters: IFilterItem[];
    preSelection?: number[];
};

const FilterDropdown: FC<FilterDropdownProps> = ({ title, filters, preSelection = [] }) => {
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { t } = useTranslation();
    const [selection, setSelection] = useState(preSelection);
    const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const { updateFilter, getFilterValue, isFilterApplied, removeFilter } = useLocalFilter();
    const open = Boolean(anchorEl);

    const id = open ? "simple-popover" : undefined;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClear = () => {
        filters.forEach((filter) => removeFilter(filter.id));
        handleClose();
    };

    const anchorOrigin: PopoverOrigin = !isMobileScreen
        ? {
              vertical: "bottom",
              horizontal: "left",
          }
        : { vertical: "bottom", horizontal: "left" };

    const transformOrigin: PopoverOrigin = !isMobileScreen
        ? {
              vertical: "top",
              horizontal: "left",
          }
        : { vertical: "top", horizontal: "left" };

    const currentFilter = filters[selectedFilterIndex];

    const localUpdateFilter = (values: LocalFilterValueType) => {
        if (currentFilter.isNull(values)) {
            removeFilter(currentFilter.id);
        } else {
            updateFilter(currentFilter.id, values);
        }
        handleClose();
    };

    const getFilterOptions = (filter: IFilterItem) => {
        const isApplied = isFilterApplied(filter.id);
        switch (filter.type) {
            case LocalFilterType.DATE_RANGE:
                return (
                    <DateRangeForm
                        initialValues={isApplied && getFilterValue(filter.id)}
                        onChange={localUpdateFilter}
                    />
                );
            default:
                return <span>Filter not found</span>;
        }
    };

    return (
        <StyledFilterDropdown>
            <div className="container">
                <div className="rowContainer">
                    <div className="dropdownContainer">
                        <Button
                            aria-labelledby="filter-label"
                            disableRipple
                            className={clsx("filterButton", selection.length > 0 && "active")}
                            onClick={handleClick}
                            data-testid="filter_dropdown-button-dropdown"
                        >
                            <FilterIcon />
                        </Button>
                        <span id="filter-label" className="filterDropdownLabel">
                            {title}
                        </span>
                    </div>
                </div>
            </div>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                classes={{ paper: "popover-paper" }}
            >
                <div className="filterOptions">
                    <Hidden smUp>
                        <MobileFilterHeader>
                            <div className="d-flex align-center">
                                <Cross onClick={handleClose} className="closeIconMobile" color="secondary" />
                                <div className="mobilePageTitle ml10">{title}</div>
                            </div>

                            <CleanFilter data-testid="filter_dropdown-button-clear" onClick={() => setSelection([])}>
                                {t("Common.Filters.Clear")}
                            </CleanFilter>
                        </MobileFilterHeader>
                        <LineSeparator />
                    </Hidden>
                    {filters.map((filter, index) => {
                        const selected = index === selectedFilterIndex;
                        const isApplied = isFilterApplied(filter.id);
                        const count = isApplied ? filter.count(getFilterValue(filter.id)) : 0;
                        return (
                            <FilterContainer
                                key={`filter-${filter.id}`}
                                selected={selected}
                                data-testid={`filter_dropdown-option-${filter.id}`}
                                onClick={() => setSelectedFilterIndex(index)}
                            >
                                <FilterTitle $selected={selected}>{filter.label}</FilterTitle>
                                {count !== 0 && (
                                    <FilterCount>
                                        <FilterCountNumber>{count}</FilterCountNumber>
                                    </FilterCount>
                                )}
                                <FilterCheck $selected={selected}>
                                    <CheckIcon />
                                </FilterCheck>
                            </FilterContainer>
                        );
                    })}
                    <LineSeparator />
                    <CurrentFilterContainer>{getFilterOptions(currentFilter)}</CurrentFilterContainer>
                </div>
                <Hidden smDown>
                    <LineSeparator />
                    <CleanFilter data-testid="filter_dropdown-button-clear" onClick={() => handleClear()}>
                        {t("Common.Filters.Clear")}
                    </CleanFilter>
                </Hidden>
            </Popover>
        </StyledFilterDropdown>
    );
};

export default FilterDropdown;
